import {
    styled,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Divider,
    Skeleton,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { TitledAppBar } from 'src/components/common/TitledAppBar';
import { DocumentSourceInputs } from 'src/components/MatchingVisualizer/DocumentSourceInput';
import { LoadResetButtons } from 'src/components/MatchingVisualizer/LoadResetButtons';
import { useMatchingVisualizerLoad } from 'src/hooks/useMatchingVisualizerLoad';
import { useDocumentSource } from 'src/hooks/useDocumentSource';
import { TargetProductOptions } from 'src/components/MatchingVisualizer/TargetProductOptions';
import { JsonViewer } from 'src/components/MatchingVisualizer/JsonViewer';
import { DEFAULT_CULTURE } from 'src/constants';
import { AssetsImages } from 'src/components/MatchingVisualizer/AssetsImages';
import { LoadingStateImage } from 'src/components/MatchingVisualizer/LoadingStateImage';
import { DestinationProductOptions } from 'src/components/MatchingVisualizer/DestinationProductOptions';
import { DesignTypeToggle } from 'src/components/MatchingVisualizer/DesignTypeToggle';
import { MatcherType, DesignCreationType } from 'src/services/DesignConductorService';
import { PreviewUrlBuilder } from 'src/utils/PreviewUrlBuilder';

const StyledContainer = styled(Box)(({ theme }) => ({
    padding: `0 ${theme.spacing(8)}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
}));

const ContentWrapper = styled(Box)(() => ({
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
}));

const TwoColumnLayout = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
}));

const LeftColumn = styled(Box)(({ theme }) => ({
    flex: `0 0 calc(40% - ${theme.spacing(2)})`,
    minHeight: 0,
    display: 'flex',
    [theme.breakpoints.down('md')]: {
        flex: '1 1 auto',
    },
}));

const RightColumn = styled(Box)(({ theme }) => ({
    flex: `0 0 calc(60% - ${theme.spacing(2)})`,
    minWidth: 0,
    overflow: 'auto',
    [theme.breakpoints.down('md')]: {
        flex: '1 1 auto',
    },
}));

const PlaceholderCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const MATCHER_TYPES: MatcherType[] = ['template', 'logo'];

type TabValue = MatcherType | 'pds';

export const MatchingVisualizerPage = (): JSX.Element => {
    const [productKey, setProductKey] = useState<string>('');
    const [version, setVersion] = useState<number | undefined>(undefined);
    const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
    const [requiredOptions, setRequiredOptions] = useState<Record<string, boolean>>({});
    const [culture, setCulture] = useState<string>(DEFAULT_CULTURE);
    const [uri, setUri] = useState('');
    const [pdsResult, setPdsResult] = useState('');
    const [showPdsResult, setShowPdsResult] = useState(false);
    const [dpcId, setDpcId] = useState('');
    const [showDpcId, setShowDpcId] = useState(false);
    const [selectedTab, setSelectedTab] = useState<TabValue>('template');
    const [selectedDesignType, setSelectedDesignType] = useState<DesignCreationType>('Static');
    const [hasAutoSelected, setHasAutoSelected] = useState(false);
    const [loadParams, setLoadParams] = useState<{
        documentSourceUri: string;
        productKey: string;
        productVersion: number;
        culture: string;
        fullProductSpecification: Record<string, string>;
        preferredProductSpecification?: Record<string, string>;
        pdsResultUri?: string;
        dpcId?: string;
    } | null>(null);

    // Shared document source query
    const documentSourceQuery = useDocumentSource(uri);

    // Only call matching hook if document source is successful
    const matchingResults = useMatchingVisualizerLoad(
        loadParams && !documentSourceQuery.error ? loadParams : null,
        documentSourceQuery,
    );

    // Auto-select first successful design type when results load
    useEffect(() => {
        // Only auto-select if we haven't already done so and we have results
        if (!hasAutoSelected && matchingResults.matchingResults.template.designTypes) {
            const { designTypes } = matchingResults.matchingResults.template;
            const designTypeOrder: DesignCreationType[] = ['Static', 'Dynamic', 'AutoLayout'];

            // Check if we have all results (successful or error)
            const haveAllResults = designTypeOrder.every((type) => designTypes[type] && (designTypes[type].data || designTypes[type].error));

            if (haveAllResults) {
                // Find first successful design type
                for (const type of designTypeOrder) {
                    if (designTypes[type]?.data && !designTypes[type]?.error) {
                        setSelectedDesignType(type);
                        setHasAutoSelected(true);
                        break;
                    }
                }
                // Even if no successful type found, mark as auto-selected to prevent re-running
                setHasAutoSelected(true);
            }
        }
    }, [matchingResults.matchingResults.template, hasAutoSelected]);

    const assetsInfo = matchingResults.dpcmResult.data?.designPersonalizationData?.images[0]?.image;
    const assetsPreviewUrl = assetsInfo?.previewUrl || '';
    const assetsPrintUrl = assetsInfo?.printUrl || '';
    const assetsOriginalUrl = assetsInfo?.originalSourceUrl || '';

    const isStaticDesign = documentSourceQuery.data?.templateIdentifier?.type === 'TEMPLATE_TOKEN';
    const templateToken = isStaticDesign
        ? (documentSourceQuery.data?.templateIdentifier?.value ?? undefined)
        : undefined;
    const originalSourceTemplateUrl = templateToken
        ? PreviewUrlBuilder.build(templateToken, '400')
        : undefined;

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        const documentUri = params.get('documentUri');
        const productKeyParam = params.get('productKey');
        const versionParam = params.get('productVersion');
        const cultureParam = params.get('culture');
        const optionsParam = params.get('options');
        const requiredParam = params.get('required');
        const pdsResultParam = params.get('siteResult');
        const dpcIdParam = params.get('dpcId');

        if (documentUri) {
            setUri(decodeURIComponent(documentUri));
        }
        if (productKeyParam) {
            setProductKey(decodeURIComponent(productKeyParam));
        }
        if (versionParam) {
            setVersion(Number(versionParam));
        }
        if (cultureParam) {
            setCulture(decodeURIComponent(cultureParam));
        }
        if (pdsResultParam) {
            setPdsResult(decodeURIComponent(pdsResultParam));
            setShowPdsResult(true);
        }
        if (dpcIdParam) {
            setDpcId(decodeURIComponent(dpcIdParam));
            setShowDpcId(true);
        }

        if (optionsParam) {
            try {
                const decoded = decodeURIComponent(optionsParam);
                const parsed = JSON.parse(decoded);

                if (parsed && typeof parsed === 'object') {
                    setSelectedOptions(parsed);
                }
            } catch {
                setSelectedOptions({});
            }
        }

        if (requiredParam) {
            try {
                const decoded = decodeURIComponent(requiredParam);
                const parsed = JSON.parse(decoded);

                if (parsed && typeof parsed === 'object') {
                    setRequiredOptions(parsed);
                }
            } catch {
                setRequiredOptions({});
            }
        }
        if (documentUri && productKeyParam && versionParam && cultureParam && optionsParam) {
            const options = JSON.parse(decodeURIComponent(optionsParam));
            const requiredMap = requiredParam ? JSON.parse(decodeURIComponent(requiredParam)) : {};

            const requiredOpts: Record<string, string> = {};
            const preferredOpts: Record<string, string> = {};

            Object.entries(options).forEach(([key, value]) => {
                if (requiredMap[key] !== false) {
                    requiredOpts[key] = value as string;
                } else {
                    preferredOpts[key] = value as string;
                }
            });

            setLoadParams({
                documentSourceUri: decodeURIComponent(documentUri),
                productKey: decodeURIComponent(productKeyParam),
                productVersion: Number(versionParam),
                culture: decodeURIComponent(cultureParam),
                fullProductSpecification: requiredOpts,
                preferredProductSpecification: Object.keys(preferredOpts).length > 0 ? preferredOpts : undefined,
                pdsResultUri: pdsResultParam ? decodeURIComponent(pdsResultParam) : undefined,
                dpcId: dpcIdParam ? decodeURIComponent(dpcIdParam) : undefined,
            });
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams();

        if (uri) {
            params.set('documentUri', encodeURIComponent(uri));
        }
        if (productKey) {
            params.set('productKey', encodeURIComponent(productKey));
        }
        if (version !== undefined) {
            params.set('productVersion', String(version));
        }
        if (culture) {
            params.set('culture', encodeURIComponent(culture));
        }
        if (Object.keys(selectedOptions).length > 0) {
            params.set('options', encodeURIComponent(JSON.stringify(selectedOptions)));
        }
        if (Object.keys(requiredOptions).length > 0) {
            params.set('required', encodeURIComponent(JSON.stringify(requiredOptions)));
        }
        if (pdsResult) {
            params.set('siteResult', encodeURIComponent(pdsResult));
        }
        if (dpcId) {
            params.set('dpcId', encodeURIComponent(dpcId));
        }

        const newUrl = `${window.location.pathname}?${params.toString()}`;

        window.history.replaceState({}, '', newUrl);
    }, [uri, productKey, version, culture, selectedOptions, requiredOptions, pdsResult, dpcId]);

    const handleLoad = (documentUri: string): void => {
        if (!productKey || !version) {
            return;
        }

        const requiredOpts: Record<string, string> = {};
        const preferredOpts: Record<string, string> = {};

        Object.entries(selectedOptions).forEach(([key, value]) => {
            if (requiredOptions[key] !== false) {
                requiredOpts[key] = value;
            } else {
                preferredOpts[key] = value;
            }
        });

        const nextParams = {
            documentSourceUri: documentUri,
            productKey,
            productVersion: version,
            culture,
            fullProductSpecification: requiredOpts,
            preferredProductSpecification: Object.keys(preferredOpts).length > 0 ? preferredOpts : undefined,
            pdsResultUri: pdsResult || undefined,
            dpcId: dpcId || undefined,
        };

        // Reset auto-selection flag so it will auto-select again for new results
        setHasAutoSelected(false);
        setLoadParams(null);
        queueMicrotask(() => setLoadParams(nextParams));
    };

    const handleReset = (): void => {
        setUri('');
        setProductKey('');
        setVersion(undefined);
        setSelectedOptions({});
        setRequiredOptions({});
        setCulture(DEFAULT_CULTURE);
        setPdsResult('');
        setShowPdsResult(false);
        setDpcId('');
        setShowDpcId(false);
        setLoadParams(null);
    };

    return (
        <>
            <TitledAppBar title="Matching Visualizer" />
            <StyledContainer>
                <ContentWrapper>
                    <TwoColumnLayout>
                        <LeftColumn>
                            <PlaceholderCard sx={{
                                flexDirection: 'column', alignItems: 'stretch', height: '100%', overflow: 'hidden',
                            }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        overflow: 'auto',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <DocumentSourceInputs
                                        documentSourceQuery={documentSourceQuery}
                                        documentUri={uri}
                                        dpcId={dpcId}
                                        pdsResult={pdsResult}
                                        showDpcId={showDpcId}
                                        showPdsResult={showPdsResult}
                                        onDocumentUriChange={(val: string): void => setUri(val)}
                                        onDpcIdChange={(val: string): void => setDpcId(val)}
                                        onPdsResultChange={(val: string): void => setPdsResult(val)}
                                        onShowDpcIdChange={(show: boolean): void => setShowDpcId(show)}
                                        onShowPdsResultChange={(show: boolean): void => setShowPdsResult(show)}
                                    />
                                    <TargetProductOptions
                                        culture={culture}
                                        initialOptions={selectedOptions}
                                        initialRequiredOptions={requiredOptions}
                                        productKey={productKey}
                                        version={version}
                                        onChangeCulture={(c): void => setCulture(c)}
                                        onChangeOptions={(opts, reqMap): void => {
                                            setSelectedOptions(opts);
                                            setRequiredOptions(reqMap);
                                        }}
                                        onChangeProductKey={(k): void => setProductKey(k)}
                                        onChangeVersion={(v): void => setVersion(v)}
                                    />
                                </Box>
                                <Box sx={{ flexShrink: 0, pt: 2 }}>
                                    <LoadResetButtons
                                        isLoading={matchingResults.isLoading}
                                        productKey={productKey}
                                        productVersion={version}
                                        uri={uri}
                                        onLoad={handleLoad}
                                        onReset={handleReset}
                                    />
                                </Box>
                            </PlaceholderCard>
                        </LeftColumn>
                        <RightColumn>
                            <PlaceholderCard sx={{
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                justifyContent: 'stretch',
                                gap: 2,
                                minHeight: '100%',
                            }}
                            >
                                {documentSourceQuery.error && (
                                    <Box sx={{ width: '100%' }}>
                                        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                                            {`Document Source Error: ${documentSourceQuery.error.message}`}
                                        </Alert>
                                    </Box>
                                )}
                                {matchingResults.isLoading && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    Matcher Type:
                                                </Typography>
                                                <Tabs>
                                                    <Tab disabled label="Template" />
                                                    <Tab disabled label="Logo" />
                                                </Tabs>
                                            </Box>
                                            <Divider sx={{ mt: 1, mb: 2 }} />
                                            <Box>
                                                {/* Design Type Toggle skeleton */}
                                                <Box sx={{ mb: 2 }}>
                                                    <Skeleton
                                                        animation="wave"
                                                        height={36.5}
                                                        sx={{ borderRadius: 1 }}
                                                        variant="rectangular"
                                                        width="100%"
                                                    />
                                                </Box>
                                                <Typography gutterBottom variant="h6">
                                                    Destination Preview
                                                </Typography>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                                                        <Skeleton
                                                            animation="wave"
                                                            height={400}
                                                            sx={{ borderRadius: 1 }}
                                                            variant="rectangular"
                                                            width={500}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Box sx={{ mt: 3 }}>
                                                <Typography gutterBottom variant="h6">
                                                    Destination Product Options
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {[1, 2, 3, 4].map((i) => (
                                                        <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                                                            <Skeleton animation="wave" height={40} width="40%" />
                                                            <Skeleton animation="wave" height={40} width="50%" />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Divider sx={{ my: 2 }} />
                                        <Box>
                                            <Typography gutterBottom variant="h6">
                                                DocumentSource Preview
                                            </Typography>
                                            <Skeleton
                                                animation="wave"
                                                sx={{
                                                    width: '100%',
                                                    height: 300,
                                                    borderRadius: 1,
                                                }}
                                                variant="rectangular"
                                            />
                                        </Box>
                                        <Box>
                                            <Skeleton animation="wave" height={32} sx={{ mb: 1 }} width="60%" />
                                            <Skeleton
                                                animation="wave"
                                                sx={{
                                                    width: '100%',
                                                    height: 150,
                                                    borderRadius: 1,
                                                }}
                                                variant="rectangular"
                                            />
                                        </Box>
                                    </Box>
                                )}
                                {matchingResults.warnings.length > 0 && (
                                    <Box sx={{ width: '100%' }}>
                                        <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
                                            Warnings occurred during matching process:
                                            {matchingResults.warnings.map((warning, index) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <div key={`warning-${index}`}>
                                                    {`- ${warning}`}
                                                </div>
                                            ))}
                                        </Alert>
                                    </Box>
                                )}
                                {matchingResults.hasError && (
                                    <Box sx={{ width: '100%' }}>
                                        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                                            Error occurred during matching process:
                                            {matchingResults.errors.map((error, index) => (
                                                error && (
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    <div key={`error-${index}`}>
                                                        {`- ${error.message}.`}
                                                    </div>
                                                )
                                            ))}
                                        </Alert>
                                    </Box>
                                )}
                                {!matchingResults.isLoading && !loadParams && !matchingResults.hasError && (
                                    <Typography color="text.secondary" textAlign="center" variant="body2">
                                        Click Load to see matching results
                                    </Typography>
                                )}
                                {loadParams && !matchingResults.isLoading
                                 && (matchingResults.dpcmResult.error) && (
                                 <Box sx={{ width: '100%' }}>
                                     <Box sx={{
                                         display: 'flex', alignItems: 'center', gap: 2, mb: 2,
                                     }}
                                     >
                                         <Typography
                                             sx={{
                                                 fontSize: '0.875rem',
                                                 fontWeight: 500,
                                                 color: 'text.secondary',
                                             }}
                                         >
                                             Matcher Type:
                                         </Typography>
                                         <Tabs value={0}>
                                             <Tab disabled label="Template" />
                                             <Tab disabled label="Logo" />
                                         </Tabs>
                                     </Box>
                                     <Divider sx={{ mb: 2 }} />
                                 </Box>
                                )}
                                {loadParams && !documentSourceQuery.error && !matchingResults.isLoading
                                 && !matchingResults.dpcmResult.error
                                  && (
                                  <Box sx={{
                                      display: 'flex', flexDirection: 'column', gap: 2, width: '100%',
                                  }}
                                  >
                                      <Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                              <Typography
                                                  sx={{
                                                      fontSize: '0.875rem',
                                                      fontWeight: 500,
                                                      color: 'text.secondary',
                                                  }}
                                              >
                                                  Matcher Type:
                                              </Typography>
                                              <Tabs
                                                  value={selectedTab}
                                                  onChange={(_, newValue): void => setSelectedTab(newValue)}
                                              >
                                                  {MATCHER_TYPES.map((type) => (
                                                      <Tab
                                                          key={type}
                                                          label={type.charAt(0).toUpperCase() + type.slice(1)}
                                                          value={type}
                                                      />
                                                  ))}
                                                  {pdsResult && (
                                                      <Tab
                                                          key="pds"
                                                          label="Site Result"
                                                          value="pds"
                                                      />
                                                  )}
                                              </Tabs>
                                          </Box>
                                          <Divider sx={{ mt: 1, mb: 2 }} />
                                          <Box>
                                              {selectedTab === 'pds' && matchingResults.pdsResultPreview.previewUrl && (
                                                  <Box>
                                                      <Typography gutterBottom variant="h6">
                                                          Site Result Preview
                                                      </Typography>
                                                      <LoadingStateImage
                                                          imageSx={{
                                                              maxHeight: '400px',
                                                              maxWidth: '100%',
                                                              width: 'auto',
                                                              objectFit: 'contain',
                                                          }}
                                                          src={matchingResults.pdsResultPreview.previewUrl}
                                                      />
                                                  </Box>
                                              )}
                                              {selectedTab === 'template' && matchingResults.matchingResults.template.designTypes && (
                                                  <>
                                                      <DesignTypeToggle
                                                          results={matchingResults.matchingResults.template.designTypes}
                                                          selectedType={selectedDesignType}
                                                          onSelectType={setSelectedDesignType}
                                                      />
                                                      {matchingResults.matchingResults.template
                                                          .designTypes[selectedDesignType]?.data && (
                                                          <>
                                                              <Box>
                                                                  <Typography gutterBottom variant="h6">
                                                                      Destination Preview
                                                                  </Typography>
                                                                  <LoadingStateImage
                                                                      imageSx={{
                                                                          maxHeight: '400px',
                                                                          maxWidth: '100%',
                                                                          width: 'auto',
                                                                          objectFit: 'contain',
                                                                      }}
                                                                      src={matchingResults.matchingResults.template
                                                                          .designTypes[selectedDesignType]?.data?.previewUrl
                                                                          ? `https:${matchingResults.matchingResults.template
                                                                              .designTypes[selectedDesignType]?.data?.previewUrl}`
                                                                          : undefined}
                                                                  />
                                                              </Box>
                                                              {matchingResults.matchingResults.template
                                                                  .designTypes[selectedDesignType]?.data?.fullProductSpecification && (
                                                                  <Box sx={{ mt: 2 }}>
                                                                      <DestinationProductOptions
                                                                          destinationOptions={
                                                                          matchingResults.matchingResults.template
                                                                              .designTypes[selectedDesignType]?.data
                                                                              ?.fullProductSpecification as Record<string, string>
                                                                      }
                                                                          inputOptions={selectedOptions}
                                                                      />
                                                                  </Box>
                                                              )}
                                                          </>
                                                      )}
                                                  </>
                                              )}
                                              {selectedTab === 'logo' && matchingResults.matchingResults.logo.data && (
                                                  <>
                                                      <Box>
                                                          <Typography gutterBottom variant="h6">
                                                              Destination Preview
                                                          </Typography>
                                                          <LoadingStateImage
                                                              imageSx={{
                                                                  maxHeight: '400px',
                                                                  maxWidth: '100%',
                                                                  width: 'auto',
                                                                  objectFit: 'contain',
                                                              }}
                                                              src={matchingResults.matchingResults
                                                                  .logo.data?.[0]?.previewUrl
                                                                  ? `https:${matchingResults.matchingResults.logo.data?.[0]?.previewUrl}`
                                                                  : undefined}
                                                          />
                                                      </Box>
                                                      {matchingResults.matchingResults
                                                          .logo.data?.[0]?.fullProductSpecification && (
                                                          <Box sx={{ mt: 2 }}>
                                                              <DestinationProductOptions
                                                                  destinationOptions={
                                                                  matchingResults.matchingResults.logo.data?.[0]
                                                                      ?.fullProductSpecification as
                                                                      Record<string, string>
                                                              }
                                                                  inputOptions={selectedOptions}
                                                              />
                                                          </Box>
                                                      )}
                                                  </>
                                              )}
                                              {selectedTab === 'template'
                                                  && matchingResults.matchingResults.template.designTypes
                                                  && matchingResults.matchingResults.template
                                                      .designTypes[selectedDesignType]?.error && (
                                                      <Box sx={{ width: '100%' }}>
                                                          <Alert
                                                              severity={
                                                                  matchingResults.matchingResults.template
                                                                      .designTypes[selectedDesignType]?.error?.message
                                                                      ?.includes('404') ? 'warning' : 'error'
                                                              }
                                                              sx={{ mt: 2, width: '100%' }}
                                                          >
                                                              {matchingResults.matchingResults.template
                                                                  .designTypes[selectedDesignType]?.error?.message
                                                                  || 'An error occurred'}
                                                          </Alert>
                                                      </Box>
                                              )}
                                              {selectedTab === 'logo'
                                                  && matchingResults.matchingResults.logo.error && (
                                                      <Box sx={{ width: '100%' }}>
                                                          <Alert
                                                              severity={
                                                                  matchingResults.matchingResults.logo
                                                                      .error?.message?.includes('404') ? 'warning' : 'error'
                                                              }
                                                              sx={{ mt: 2, width: '100%' }}
                                                          >
                                                              {matchingResults.matchingResults.logo
                                                                  .error?.message || 'An error occurred'}
                                                          </Alert>
                                                      </Box>
                                              )}
                                              {selectedTab === 'template'
                                                  && matchingResults.matchingResults.template.designTypes
                                                  && matchingResults.matchingResults.template
                                                      .designTypes[selectedDesignType]?.isLoading && (
                                                      <Box alignItems="center" display="flex" justifyContent="center" p={4}>
                                                          <CircularProgress size={20} />
                                                          <Typography ml={2}>
                                                              Loading
                                                              {' '}
                                                              {selectedDesignType}
                                                              {' '}
                                                              results...
                                                          </Typography>
                                                      </Box>
                                              )}
                                              {selectedTab === 'logo'
                                                  && matchingResults.matchingResults.logo.isLoading && (
                                                      <Box alignItems="center" display="flex" justifyContent="center" p={4}>
                                                          <CircularProgress size={20} />
                                                          <Typography ml={2}>
                                                              Loading
                                                              {' '}
                                                              {selectedTab}
                                                              {' '}
                                                              results...
                                                          </Typography>
                                                      </Box>
                                              )}
                                          </Box>
                                      </Box>

                                      {/* Divider to separate dynamic from static content */}
                                      <Divider sx={{ my: 2 }} />

                                      {matchingResults.documentSourcePreviewUrl && (
                                      <Box>
                                          <Typography gutterBottom variant="h6">
                                              Document Source Preview
                                          </Typography>
                                          <LoadingStateImage
                                              imageSx={{
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  borderRadius: 1,
                                                  maxHeight: '400px',
                                                  maxWidth: '100%',
                                                  width: 'auto',
                                                  objectFit: 'contain',
                                              }}
                                              src={matchingResults.documentSourcePreviewUrl}
                                          />
                                      </Box>
                                      )}
                                      {isStaticDesign && originalSourceTemplateUrl && (
                                      <Box>
                                          <Typography gutterBottom variant="h6">
                                              Original Source Template Preview
                                          </Typography>
                                          <LoadingStateImage
                                              imageSx={{
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  borderRadius: 1,
                                                  maxHeight: '400px',
                                                  maxWidth: '100%',
                                                  width: 'auto',
                                                  objectFit: 'contain',
                                              }}
                                              src={originalSourceTemplateUrl}
                                          />
                                      </Box>
                                      )}
                                      {matchingResults.dpcmResult.data && (
                                      <JsonViewer
                                          data={matchingResults.dpcmResult.data}
                                          title="Design Personalization Context"
                                      />
                                      )}
                                      {matchingResults.dpcmResult.data && (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                          <Typography gutterBottom variant="h6">
                                              Extracted Images / Logos
                                          </Typography>
                                          <AssetsImages
                                              assetsOriginalUrl={assetsOriginalUrl}
                                              assetsPreviewUrl={assetsPreviewUrl}
                                              assetsPrintUrl={assetsPrintUrl}
                                          />
                                      </Box>
                                      )}
                                  </Box>
                                  )}
                            </PlaceholderCard>
                        </RightColumn>
                    </TwoColumnLayout>
                </ContentWrapper>
            </StyledContainer>
        </>
    );
};
